import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { ChevronLeft, Headset, Loader2, Search } from 'lucide-react';
import { API_URL } from '../../config';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import SuperAdminShell from './SuperAdminShell';
import TicketStatusBadge from '../../components/support/TicketStatusBadge';
import TicketDetailPanel from '../../components/support/TicketDetailPanel';
import { TICKET_STATUSES, formatTicketDate } from '../../lib/supportConstants';

function AdminTicketRow({ row, selected, onSelect }) {
  return (
    <button
      type="button"
      onClick={() => onSelect(row.id)}
      className={`w-full text-left px-3 py-3 transition-colors border-b border-slate-100 dark:border-slate-800 hover:bg-slate-100/80 dark:hover:bg-slate-800/60 ${
        selected ? 'bg-blue-50 dark:bg-blue-950/40' : ''
      }`}
    >
      <div className="flex items-start justify-between gap-2">
        <span className={`text-sm truncate ${selected ? 'font-semibold' : 'font-medium'} text-slate-900 dark:text-white`}>
          {row.subject}
        </span>
        <span className="shrink-0 text-[10px] font-mono text-slate-400">#{row.ticket_number}</span>
      </div>
      <p className="text-xs text-slate-500 truncate mt-0.5">{row.organization_name}</p>
      <p className="text-xs text-slate-500 truncate">{row.created_by_email}</p>
      <div className="flex flex-wrap items-center gap-2 mt-1.5">
        <TicketStatusBadge status={row.status} />
        <span className="text-[11px] text-slate-400">{formatTicketDate(row.last_message_at || row.updated_at)}</span>
      </div>
    </button>
  );
}

export default function SuperAdminSupportPage() {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [q, setQ] = useState('');
  const [debouncedQ, setDebouncedQ] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [mobileShowDetail, setMobileShowDetail] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(q.trim()), 350);
    return () => clearTimeout(t);
  }, [q]);

  const loadTickets = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/platform/support/tickets`, {
        params: {
          q: debouncedQ || undefined,
          status: statusFilter || undefined,
        },
      });
      setTickets(res.data || []);
    } catch {
      toast.error('Failed to load support tickets');
      setTickets([]);
    } finally {
      setLoading(false);
    }
  }, [debouncedQ, statusFilter]);

  useEffect(() => {
    loadTickets();
  }, [loadTickets]);

  const loadDetail = useCallback(async (ticketId, { silent = false } = {}) => {
    if (!ticketId) {
      setDetail(null);
      return;
    }
    if (silent) setRefreshing(true);
    else setDetailLoading(true);
    try {
      const res = await axios.get(`${API_URL}/platform/support/tickets/${ticketId}`);
      setDetail(res.data);
    } catch {
      toast.error('Failed to load ticket');
      if (!silent) setDetail(null);
    } finally {
      if (silent) setRefreshing(false);
      else setDetailLoading(false);
    }
  }, []);

  useEffect(() => {
    if (selectedId) loadDetail(selectedId);
    else setDetail(null);
  }, [selectedId, loadDetail]);

  const selectTicket = (id) => {
    setSelectedId(id);
    setMobileShowDetail(true);
  };

  const handleRefresh = async () => {
    if (!selectedId) return;
    await loadDetail(selectedId, { silent: true });
    await loadTickets();
  };

  const handleReply = async (body, isInternal) => {
    if (!selectedId) return;
    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/platform/support/tickets/${selectedId}/messages`, {
        body,
        is_internal: isInternal,
      });
      await loadDetail(selectedId);
      await loadTickets();
      toast.success(isInternal ? 'Note added' : 'Reply sent');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to send');
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdateMeta = async (patch) => {
    if (!selectedId) return;
    try {
      const res = await axios.patch(`${API_URL}/platform/support/tickets/${selectedId}`, patch);
      setDetail((prev) => (prev ? { ...prev, ...res.data } : prev));
      setTickets((prev) => prev.map((t) => (t.id === selectedId ? { ...t, ...res.data } : t)));
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Update failed');
    }
  };

  return (
    <SuperAdminShell
      wide
      inbox
      title="Help Center"
      description="Customer feedback, bugs, and enhancement requests from all workspaces."
      testId="super-admin-support"
    >
      <div className="flex h-[calc(100dvh-9.5rem)] min-h-[480px] rounded-xl border border-slate-200 dark:border-slate-800 overflow-hidden bg-white dark:bg-slate-950 shadow-sm">
        <aside
          className={`flex flex-col w-full md:w-[min(380px,38%)] lg:w-[360px] shrink-0 border-r border-slate-200 dark:border-slate-800 min-h-0 ${
            mobileShowDetail ? 'hidden md:flex' : 'flex'
          }`}
        >
          <div className="shrink-0 p-3 border-b border-slate-200 dark:border-slate-800 space-y-2">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                className="pl-9 h-9 bg-slate-50 dark:bg-slate-800/50"
                placeholder="Search tickets…"
                value={q}
                onChange={(e) => setQ(e.target.value)}
              />
            </div>
            <select
              className="flex h-8 w-full rounded-md border border-input bg-transparent px-2 text-xs"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <option value="">All statuses</option>
              {TICKET_STATUSES.map((s) => (
                <option key={s.value} value={s.value}>
                  {s.label}
                </option>
              ))}
            </select>
          </div>
          <div className="flex-1 min-h-0 overflow-y-auto">
            {loading ? (
              <div className="flex items-center justify-center gap-2 text-slate-500 py-16">
                <Loader2 className="w-4 h-4 animate-spin" />
              </div>
            ) : tickets.length === 0 ? (
              <p className="text-sm text-slate-500 py-16 px-4 text-center">No tickets match.</p>
            ) : (
              tickets.map((row) => (
                <AdminTicketRow key={row.id} row={row} selected={selectedId === row.id} onSelect={selectTicket} />
              ))
            )}
          </div>
        </aside>

        <main
          className={`flex-1 min-w-0 flex flex-col min-h-0 ${
            !mobileShowDetail && !selectedId ? 'hidden md:flex' : 'flex'
          }`}
        >
          {mobileShowDetail && selectedId ? (
            <div className="md:hidden shrink-0 flex items-center gap-2 px-3 py-2 border-b border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900">
              <Button variant="ghost" size="sm" className="h-8 -ml-2" onClick={() => setMobileShowDetail(false)}>
                <ChevronLeft className="w-4 h-4 mr-1" />
                Tickets
              </Button>
            </div>
          ) : null}

          {!selectedId ? (
            <div className="flex-1 flex flex-col items-center justify-center text-center px-8 text-slate-500">
              <div className="rounded-full bg-slate-100 dark:bg-slate-800 p-5 mb-4">
                <Headset className="w-10 h-10 text-slate-400" />
              </div>
              <p className="text-sm font-medium text-slate-700 dark:text-slate-300">Select a ticket</p>
              <p className="text-xs mt-1 max-w-xs">Review customer feedback and reply from the queue.</p>
            </div>
          ) : (
            <TicketDetailPanel
              ticket={detail}
              mode="admin"
              orgId={detail?.organization_id}
              loading={detailLoading}
              refreshing={refreshing}
              submitting={submitting}
              onRefresh={handleRefresh}
              onReply={handleReply}
              onUpdateMeta={handleUpdateMeta}
            />
          )}
        </main>
      </div>
    </SuperAdminShell>
  );
}
